<template>
    <card-tabs selected-name="pipelines">
        <template #labels>
            <card-tab-label
                v-slot="{ bindings }"
                name="pipelines"
            >
                <text-decorator
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    :size="FontSize.sm"
                    bold
                >
                    Pijplijn
                </text-decorator>
                <chip-informative
                    v-if="canViewPotentialOrderValue"
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #prefix>
                        {{ leadCollection.count }}
                    </template>
                    <template #default>
                        {{ formatMoneyShort(leadValue) }}
                    </template>
                </chip-informative>
                <chip-informative
                    v-else
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #default>
                        {{ leadCollection.count }}
                    </template>
                </chip-informative>
            </card-tab-label>

            <card-tab-label
                v-if="currentLegacyCompany?.orderSettings?.enabled"
                v-slot="{ bindings }"
                name="orders"
            >
                <text-decorator
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    :size="FontSize.sm"
                    bold
                >
                    Opdracht
                </text-decorator>
                <chip-informative
                    v-if="canViewPotentialOrderValue"
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    small
                >
                    <template #prefix>
                        {{ orderCount }}
                    </template>
                    <template #default>
                        {{ formatMoneyShort(orderValue) }}
                    </template>
                </chip-informative>
                <chip-informative
                    v-else
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #default>
                        {{ orderCount }}
                    </template>
                </chip-informative>
            </card-tab-label>
        </template>

        <template #panels>
            <card-tab-panel
                with-variable-height
                without-padding
                name="pipelines"
            >
                <pipeline-list />
            </card-tab-panel>

            <card-tab-panel
                v-if="currentLegacyCompany?.orderSettings?.enabled"
                with-variable-height
                without-padding
                name="orders"
            >
                <order-list />
            </card-tab-panel>
        </template>
    </card-tabs>
</template>

<script setup lang="ts">
    import {
        Color,
        CardTabs,
        CardTabLabel,
        CardTabPanel,
        ChipInformative,
        FontSize,
    } from '@keyvue/one-ui';

    import OrderList from '@views/Contact/Detail/Lists/OrderList.vue';
    import PipelineList from '@views/Contact/Detail/Lists/PipelineList.vue';
    import { Contact, Lead } from '@keyvue/one-api-client';
    import { ref, computed, provide, reactive, type Ref } from 'vue';
    import { Branch, Order, OrderAPI } from '@keyvue/one-api-client-legacy';
    import { formatMoneyShort } from '@/utils/money';
    import useClient, { type ReactiveCollection } from '@/composables/useClient';
    import { onBeforeRouteUpdate } from 'vue-router';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        contactId: string;
    }

    const props = defineProps<Props>();
    const {
        getReactiveCollection,
        currentLegacyCompany,
        currentLegacyUser,
        client,
        currentCompany,
    } = useClient();
    const eventBus = useEventBus();

    const contact = reactive(await client.get(Contact, props.contactId)) as Contact;
    provide('contact', contact);

    const [
        leadCollection,
        orders,
    ]: [
        ReactiveCollection<Lead>,
        Ref<Order[]>,
    ] = await Promise.all([
        getReactiveCollection(Lead, { primaryContact: props.contactId }),
        ref(await OrderAPI.getAll({ 'referral.contact': props.contactId })) as Ref<Order[]>,
    ]);

    provide('leadCollection', leadCollection);
    provide('orders', orders);

    const canViewPotentialOrderValue = computed(() => (
        currentLegacyCompany.value?.potentialOrderValueRoles.includes(currentLegacyUser.value?.role?.toString() ?? '')
    ));

    const valuableLeadCollection = computed(() => leadCollection.data.value.filter(
        (lead) => (lead.assignee.company.equals(currentCompany.value)),
    ));
    const leadValue = computed(() => valuableLeadCollection.value.reduce((acc, lead) => acc + (lead.potentialOrderValue ?? 0), 0));
    const orderCount = computed(() => orders.value.length);
    const valuableOrders = computed(() => orders.value.filter(
        (order) => {
            if (order.referral.assignedToBranch instanceof Branch) {
                return order.referral.assignedToBranch.company?.id === currentCompany.value?.id.value;
            }

            return false;
        },
    ));
    const orderValue = computed(() => valuableOrders.value.reduce((acc, order) => acc + (order.orderValue ?? 0), 0));

    onBeforeRouteUpdate(async (to) => {
        if (to.name !== 'contactDetail') {
            return;
        }

        await Promise.all([
            leadCollection.load(),
        ]);
    });

    const eventBusCallback = async (params) => {
        if (params === 'Lead') {
            await leadCollection.load();
        }

        if (params === 'Order') {
            orders.value = await OrderAPI.getAll({ 'referral.contact': props.contactId });
        }
    };

    eventBus.subscribe<string>('resource-manipulated', eventBusCallback);
    eventBus.subscribe<string>('resource-deleted', eventBusCallback);
</script>
